import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { HighlightedCode, useDocumentTheme, useHighlightedCommands } from './highlighted-command';

interface EditableCommandProps {
  /** The committed command. The parent owns this value. */
  value: string;
  /** Called with the next value as the command is edited. */
  onChange: (value: string) => void;
  /** Placeholder shown when the command is empty. */
  placeholder?: string;
  /**
   * Accessible label for the click-to-edit display button. Defaults to the
   * command itself (or the placeholder when empty), but pass an explicit label
   * when the field needs a stable name independent of its value.
   */
  ariaLabel?: string;
  className?: string;
}

/**
 * A single click-to-edit shell command, styled to match a {@link CommandBlock}
 * row: a `$` prompt gutter and the syntax-highlighted command in monospace.
 * Clicking the command reveals an inline input (Enter or blur commits, Escape
 * exits); an empty field shows the placeholder until clicked.
 *
 * The single-value counterpart to {@link EditableCommandList}, for form fields
 * that hold exactly one command (e.g. a blueprint's install command). The value
 * is fully controlled — edits commit to `onChange` live — so no draft can be
 * lost when the surrounding form submits while the input is focused.
 */
export function EditableCommand({
  value,
  onChange,
  placeholder = 'npm install',
  ariaLabel,
  className,
}: EditableCommandProps) {
  const [editing, setEditing] = useState(false);

  const theme = useDocumentTheme();
  const tokens = useHighlightedCommands(value ? [value] : [], theme)[0];

  if (editing) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border border-ring bg-muted/30 px-2.5 py-1.5 ring-[3px] ring-ring/50',
          className,
        )}
      >
        <span
          aria-hidden
          className="shrink-0 select-none font-mono text-xs leading-relaxed text-muted-foreground/40"
        >
          $
        </span>
        <Input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
          onBlur={() => setEditing(false)}
          placeholder={placeholder}
          className="h-auto flex-1 border-0 bg-transparent p-0 font-mono text-xs leading-relaxed shadow-none focus-visible:ring-0 md:text-xs"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5',
        className,
      )}
    >
      <span
        aria-hidden
        className="shrink-0 select-none font-mono text-xs leading-relaxed text-muted-foreground/40"
      >
        $
      </span>
      <button
        type="button"
        // The command renders as syntax-highlighted token spans (no single
        // matchable text node), so the raw command — or the placeholder when
        // empty — is the button's accessible name for screen readers and tests.
        aria-label={ariaLabel ?? (value || placeholder)}
        className={cn(
          'min-w-0 flex-1 cursor-text truncate text-left font-mono text-xs leading-relaxed',
          value ? !tokens && 'text-foreground/90' : 'text-muted-foreground/50',
        )}
        onClick={() => setEditing(true)}
      >
        {value ? tokens ? <HighlightedCode tokens={tokens} /> : value : placeholder}
      </button>
      <Pencil
        aria-hidden
        className="h-3 w-3 shrink-0 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground"
      />
    </div>
  );
}
